import { useCallback, useMemo, useRef, useState } from "react";

import { COMPLETED_COLUMN_ID, KANBAN_COLUMN_LABELS, KANBAN_COLUMN_ORDER, UNASSIGNED_COLUMN_ID } from "../../constants";
import { KanbanBoardProps, KanbanColumnId, KanbanColumnModel, KanbanDragState } from "../../models";
import { groupTasksByColumn } from "../../utils";

const EMPTY_DRAG_STATE: KanbanDragState = { draggingIds: [], dropColumn: null, dropIndex: null, height: null };

interface UseKanbanBoardResult {
  columns: KanbanColumnModel[];
  dragState: KanbanDragState;
  selectedIds: Set<string>;
  toggleSelection: (taskId: string) => void;
  clearSelection: () => void;
  /** How many cards a drag starting on `taskId` will move (the selection size, or 1). */
  resolveDragCount: (taskId: string) => number;
  onDragStart: (taskId: string, height: number) => void;
  onDragEnd: () => void;
  onColumnDragOver: (columnId: KanbanColumnId, index: number) => void;
  onColumnDragLeave: (columnId: KanbanColumnId) => void;
  onColumnDrop: (columnId: KanbanColumnId) => void;
}

export const useKanbanBoard = (props: KanbanBoardProps): UseKanbanBoardResult => {
  const { tasks, hideCompleted = false, hideUnassigned = false, lanes, onTaskDrop, onSelectionChange } = props;

  const [dragState, setDragState] = useState<KanbanDragState>(EMPTY_DRAG_STATE);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const selectedIdsRef = useRef(selectedIds);
  selectedIdsRef.current = selectedIds;
  const onSelectionChangeRef = useRef(onSelectionChange);
  onSelectionChangeRef.current = onSelectionChange;

  const columns = useMemo<KanbanColumnModel[]>(() => {
    const grouped = groupTasksByColumn(tasks);
    return KANBAN_COLUMN_ORDER.filter(
      (columnId) =>
        !(hideCompleted && columnId === COMPLETED_COLUMN_ID) && !(hideUnassigned && columnId === UNASSIGNED_COLUMN_ID)
    ).map((columnId) => {
      // Server paging state for the lane, when the host pages the cards rather than passing them all.
      const lane = lanes?.[columnId];

      return {
        id: columnId,
        label: KANBAN_COLUMN_LABELS[columnId],
        tasks: grouped[columnId],
        totalCount: lane?.totalCount,
        hasMoreTasks: lane?.hasNextPage,
        isLoadingTasks: lane?.isLoadingMore,
      };
    });
  }, [tasks, hideCompleted, hideUnassigned, lanes]);

  // Ref mirroring the rendered columns so the (stable) drag handlers can read what's currently on
  // screen without widening their dependency lists.
  const columnsRef = useRef(columns);
  columnsRef.current = columns;

  // ── Selection (shift / ⌘ / Ctrl-click) ──────────────────────────────────────
  const commitSelection = useCallback((next: Set<string>): void => {
    selectedIdsRef.current = next;
    setSelectedIds(next);
    onSelectionChangeRef.current?.(Array.from(next));
  }, []);

  const toggleSelection = useCallback(
    (taskId: string): void => {
      const next = new Set(selectedIdsRef.current);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      commitSelection(next);
    },
    [commitSelection]
  );

  const clearSelection = useCallback((): void => {
    if (selectedIdsRef.current.size === 0) return;
    commitSelection(new Set());
  }, [commitSelection]);

  const resolveDragCount = useCallback((taskId: string): number => {
    const selection = selectedIdsRef.current;
    return selection.has(taskId) ? selection.size : 1;
  }, []);

  // ── Drag and drop ─────────────────────────────────────────────────────────
  const dropTargetRef = useRef<{ columnId: KanbanColumnId; index: number } | null>(null);
  const draggingIdsRef = useRef<string[]>([]);
  // Keep the latest onTaskDrop without making the drop handler unstable.
  const onTaskDropRef = useRef(onTaskDrop);
  onTaskDropRef.current = onTaskDrop;

  // Handlers are wrapped in useCallback so their identity is stable across drag re-renders,
  // which lets the memoised columns/cards skip re-rendering while a card is being dragged.
  const onDragStart = useCallback(
    (taskId: string, height: number): void => {
      dropTargetRef.current = null;
      const selection = selectedIdsRef.current;
      // Grabbing a selected card drags the whole selection; grabbing an unselected one is a
      // single drag and drops any existing selection. Intersect with the rendered cards so a
      // selection made before a filter/hide toggle can't drag (and restatus) cards that are no
      // longer on screen.
      const visibleIds = new Set(columnsRef.current.flatMap((column) => column.tasks.map((task) => task.id)));
      const ids = selection.has(taskId) ? Array.from(selection).filter((id) => visibleIds.has(id)) : [taskId];
      draggingIdsRef.current = ids;
      if (!selection.has(taskId) && selection.size > 0) {
        commitSelection(new Set());
      }
      // Defer hiding the source cards. Chrome cancels the drag if the drag source is hidden
      // (display: none) synchronously within dragstart, so we let the drag image capture first.
      window.setTimeout(() => {
        setDragState({ draggingIds: ids, dropColumn: null, dropIndex: null, height });
      }, 0);
    },
    [commitSelection]
  );

  const onDragEnd = useCallback((): void => {
    dropTargetRef.current = null;
    draggingIdsRef.current = [];
    setDragState(EMPTY_DRAG_STATE);
  }, []);

  const onColumnDragOver = useCallback((columnId: KanbanColumnId, index: number): void => {
    dropTargetRef.current = { columnId, index };
    setDragState((prev) =>
      prev.dropColumn === columnId && prev.dropIndex === index
        ? prev
        : { ...prev, dropColumn: columnId, dropIndex: index }
    );
  }, []);

  const onColumnDragLeave = useCallback((columnId: KanbanColumnId): void => {
    setDragState((prev) => (prev.dropColumn === columnId ? { ...prev, dropColumn: null, dropIndex: null } : prev));
  }, []);

  const onColumnDrop = useCallback(
    (columnId: KanbanColumnId): void => {
      const ids = draggingIdsRef.current;
      const target = dropTargetRef.current;
      if (ids.length > 0 && target && target.columnId === columnId) {
        const draggingSet = new Set(ids);
        // The drop index counts the column's rendered cards, excluding the ones being dragged, so
        // resolve the anchor from that same list — null means "past the last visible card".
        const rendered = (columnsRef.current.find((col) => col.id === columnId)?.tasks ?? []).filter(
          (task) => !draggingSet.has(task.id)
        );
        const beforeTaskId = target.index < rendered.length ? rendered[target.index].id : null;
        // The dashboard owns the task list (it is shared with the Gantt and List views), so the
        // reorder itself is applied there rather than here.
        onTaskDropRef.current?.({ taskIds: ids, toStatus: columnId, beforeTaskId, toIndex: target.index });
        commitSelection(new Set());
      }
      onDragEnd();
    },
    [onDragEnd, commitSelection]
  );

  return {
    columns,
    dragState,
    selectedIds,
    toggleSelection,
    clearSelection,
    resolveDragCount,
    onDragStart,
    onDragEnd,
    onColumnDragOver,
    onColumnDragLeave,
    onColumnDrop,
  };
};
